import uuidv4 from "uuid/v4"

export default {
  namespaced: true,

  state: {
    id: "",
    users: [],
    userIds: [],
    messages: [],
    messageIds: [],
    pendingMessages: [],
    pendingMessagesIds: [],
    loaded: false,
    hideInput: false,
    modViewCredentials: null
  },

  mutations: {
    setChatId(state, payload) {
      state.id = payload
    },
    setChatUsers(state, payload) {
      state.users = payload
      state.userIds = payload.map(user => user.id)
    },
    setChatMessages(state, payload) {
      state.messages = payload
      state.messageIds = payload.map(message => message.id)
    },
    setChatPendingMessages(state, payload) {
      state.pendingMessages = payload
      state.pendingMessagesIds = payload.map(message => message.id)
    },
    setChatLoaded(state, payload) {
      state.loaded = payload
    },
    addChatUser(state, payload) {
      state.users = [...state.users, payload]
      state.userIds = [...state.userIds, payload.id]
    },
    addChatMessage(state, payload) {
      state.messageIds = [...state.messageIds, payload.id]
      state.messages = [...state.messages, payload]
    },
    addPendingMessage(state, payload) {
      state.pendingMessagesIds = [...state.pendingMessagesIds, payload.id]
      state.pendingMessages = [...state.pendingMessages, payload]
    },
    setHideInput(state, payload) {
      state.hideInput = payload
    },
    setModViewCredentials(state, payload) {
      state.modViewCredentials = payload
    }
  },
  actions: {
    async init({ rootState, commit, dispatch }, partyId) {
      const usersData = await rootState.chatAPI.getUsers(partyId)
      const messagesData = await rootState.chatAPI.getMessages(partyId)
      const pendingMessagesData = await rootState.chatAPI.getPendingMessages(
        partyId
      )

      let messages = [],
        users = [],
        pendingMessages = []

      // TODO: Remove next bit when back-end creates firebase collections on party start
      if (
        pendingMessagesData == undefined ||
        usersData == undefined ||
        messagesData == undefined
      ) {
        rootState.db
          .collection("users")
          .doc(partyId)
          .set({
            id: partyId,
            created: Date.now(),
            users: []
          })
        rootState.db
          .collection("messages")
          .doc(partyId)
          .set({
            id: partyId,
            created: Date.now(),
            messages: []
          })
        rootState.db
          .collection("pendingMessages")
          .doc(partyId)
          .set({
            id: partyId,
            created: Date.now(),
            pendingMessages: []
          })
      } else {
        messages = messagesData.messages
        pendingMessages = pendingMessagesData.pendingMessages
        users = usersData.users
      }

      commit("setChatId", partyId)
      commit("setChatMessages", messages)
      commit("setChatPendingMessages", pendingMessages)
      commit("setChatUsers", users)
      commit("setChatLoaded", true)
      dispatch("setupListeners", partyId)
      dispatch("getIdentity")
    },
    setupListeners({ dispatch, commit, rootState }, partyId) {
      rootState.chatAPI.onNewPendingMessages(partyId, pendingMessages => {
        commit("setChatPendingMessages", pendingMessages)
      })

      rootState.chatAPI.onNewMessage(partyId, messages => {
        commit("setChatMessages", messages)
      })

      rootState.chatAPI.onNewUsers(partyId, users => {
        users.forEach(user => dispatch("addUser", user))
      })
    },

    addMessage({ state, commit }, message) {
      if (!state.messageIds.includes(message.id)) {
        commit("addChatMessage", message)
      }
    },

    addPendingMessage({ state, commit }, message) {
      if (!state.pendingMessagesIds.includes(message.id)) {
        commit("addPendingMessage", message)
      }
    },

    addUser({ state, commit }, user) {
      if (!state.userIds.includes(user.id)) {
        commit("addChatUser", user)
      }
    },

    sendPendingMessage({ state, commit, rootState }, [text, userId, userName]) {
      commit("setHideInput", true)
      setTimeout(() => {
        commit("setHideInput", false)
      }, 10000)

      window.snowplow(
        "trackStructEvent",
        "chat",
        "pendingMessage",
        undefined,
        text
      )

      rootState.chatAPI.setPendingMessages(state.id, [
        ...state.pendingMessages,
        {
          id: uuidv4(),
          created: Date.now(),
          userId,
          userName,
          text,
          colorCode: rootState.identityStore.colorCode
        }
      ])
    },
    approveMessage(
      { state, rootState },
      [text, userId, userName, colorCode, messageId]
    ) {
      window.snowplow(
        "trackStructEvent",
        "chat",
        "acceptedMessage",
        undefined,
        text
      )

      rootState.chatAPI.setPendingMessages(
        state.id,
        state.pendingMessages.filter(message => message.id != messageId)
      )

      rootState.chatAPI.setMessages(state.id, [
        ...state.messages,
        {
          id: uuidv4(),
          created: Date.now(),
          userId,
          userName,
          text,
          colorCode
        }
      ])
    },
    undoMessage(
      { state, rootState },
      [text, userId, userName, colorCode, messageId]
    ) {
      rootState.chatAPI.setPendingMessages(state.id, [
        ...state.pendingMessages,
        {
          id: uuidv4(),
          created: Date.now(),
          userId,
          userName,
          text,
          colorCode
        }
      ])

      rootState.chatAPI.setMessages(
        state.id,
        state.messages.filter(message => message.id != messageId)
      )
    },
    sendMessage(
      { state, rootState },
      [text, userId, userName, removeFromPending, messageId]
    ) {
      let colorCode = rootState.identityStore.colorCode || 1
      window.snowplow(
        "trackStructEvent",
        "chat",
        "acceptedMessage",
        undefined,
        text
      )

      if (removeFromPending) {
        rootState.chatAPI.setPendingMessages(
          state.id,
          state.pendingMessages.filter(message => message.id != messageId)
        )
      }

      rootState.chatAPI.setMessages(state.id, [
        ...state.messages,
        {
          id: uuidv4(),
          created: Date.now(),
          userId,
          userName,
          text,
          colorCode
        }
      ])
    },

    getIdentity({ state, dispatch, rootState }) {
      if (
        rootState.identityStore.localStorageId != "" &&
        state.userIds.includes(rootState.identityStore.localStorageId)
      ) {
        const { name, email, colorCode } = state.users.find(
          user => user.id == rootState.identityStore.localStorageId
        )
        dispatch(
          "identityStore/setIdentityFromFirebase",
          [name, email, colorCode],
          { root: true }
        )
      }
    },

    sendUser({ state, rootState, dispatch }, [name, email, localStorageId]) {
      const colorCode = Math.floor(Math.random() * 18) + 1

      if (!state.userIds.includes(localStorageId)) {
        rootState.chatAPI.setUsers(state.id, [
          ...state.users,
          { id: localStorageId, created: Date.now(), name, email, colorCode }
        ])

        dispatch(
          "identityStore/setIdentityFromFirebase",
          [name, email, colorCode],
          { root: true }
        )
      }
    }
  },

  getters: {
    highlightedMessages: state =>
      state.messages.filter(message => message.isHighlighted === true)
  }
}
